
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Home, RefreshCw, ArrowLeft } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Question } from "@/types/test";
import { Separator } from "@/components/ui/separator";
import { useTestData } from "@/hooks/useTestData";
import { useEffect } from "react";
import { scrollToTop } from "@/utils/scrollUtils";

interface ProfessionalScorecardProps {
  score: number;
  category: string;
  difficulty: string;
  testId: string;
  questions?: Question[];
  answers?: Record<number, { index: number; value?: string }>;
}

export const ProfessionalScorecard = ({
  score,
  category,
  difficulty,
  testId,
  questions = [],
  answers = {},
}: ProfessionalScorecardProps) => {
  const navigate = useNavigate();
  const { data: testData } = useTestData(testId);
  
  // Ensure we start at the top of the page when the scorecard loads
  useEffect(() => {
    scrollToTop();
  }, []);
  
  const getRecommendations = (score: number): string[] => {
    if (score >= 90) {
      return [
        "Excellent progress! Continue with current activities to maintain development.",
        "Consider moving to the next stage for additional challenges."
      ];
    } else if (score >= 70) {
      return [
        "Good progress! Keep practicing current activities.",
        "Focus on areas where you noticed hesitation during the assessment."
      ];
    } else {
      return [
        "Continue practicing these skills regularly.",
        "Consider repeating this stage's activities before moving forward.",
        "Consult with your pediatrician about your child's development."
      ];
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 90) return "text-green-600";
    if (score >= 70) return "text-yellow-600";
    return "text-red-600";
  };

  const isAnswerCorrect = (question: Question, answer: { index: number; value?: string }) => {
    if (typeof question.correctAnswer === 'string') {
      return question.options[answer.index] === question.correctAnswer;
    }
    return answer.index === question.correctAnswer;
  };

  return (
    <div className="space-y-6">
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle className="text-xl md:text-2xl font-bold text-center">Assessment Results</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 md:space-y-8">
          <div className="text-center">
            <span className={`text-3xl md:text-4xl font-bold ${getScoreColor(score)}`}>
              {score}%
            </span>
          </div>
          
          <div className="flex flex-col space-y-2">
            <p className="text-base md:text-lg font-semibold text-center">
              {testData?.grade_level} | {category}
            </p>
            <p className="text-sm md:text-base text-muted-foreground text-center">{testData?.title}</p>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm md:text-base font-semibold">Recommendations</h3>
            {getRecommendations(score).map((recommendation, index) => (
              <Alert key={index}>
                <AlertDescription className="text-sm">{recommendation}</AlertDescription>
              </Alert>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Button 
              size="default"
              className="flex-1 text-sm h-12 sm:h-10"
              onClick={() => navigate(`/test/${testId}`, {
                replace: true,
                state: { reset: true }
              })}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Retake Test
            </Button>
            <Button 
              variant="outline"
              size="default"
              className="flex-1 text-sm h-12 sm:h-10"
              onClick={() => navigate("/testing-modules")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Modules
            </Button>
            <Button 
              variant="outline"
              size="default"
              className="flex-1 text-sm h-12 sm:h-10"
              onClick={() => navigate("/dashboard")}
            >
              <Home className="mr-2 h-4 w-4" />
              Dashboard
            </Button>
          </div>

          <Separator />

          <div className="space-y-4">
            <h3 className="text-lg md:text-xl font-semibold">Review Questions</h3>
            <div className="grid gap-3">
              {questions.map((question, index) => {
                const answer = answers[question.id];
                const isCorrect = answer ? isAnswerCorrect(question, answer) : false;
                return (
                  <Card key={question.id} className={`border-l-4 ${
                    isCorrect ? 'border-l-green-500' : 'border-l-red-500'
                  }`}>
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center gap-2 md:gap-3">
                        <span className="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full bg-gray-100 flex items-center justify-center font-semibold text-gray-700 text-sm md:text-base">
                          {index + 1}
                        </span>
                        <div>
                          <p className="text-sm md:text-base font-medium">{question.text}</p>
                          <p className="text-xs md:text-sm text-gray-600 mt-1">
                            {answer?.value || question.options[answer?.index || 0] || "No answer provided"}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
